import type {
    ConversationKpis,
    OwnerTeam,
    TeamAgeBucket,
    TeamStats,
    TeamStatsBucket
} from '../../services/homeStats';
import { formatDuration } from '../monitoring/overview/overviewTime.ts';

// Presentation helpers for the Home team workload cards, all derived from the
// single cached /team-stats response.

// Ortho- and GP-owned steps are not ODO workload, so neither team gets a card.
export const ALL_TEAMS: readonly OwnerTeam[] = ['Admin', 'Clinical', 'DM', 'TC'];

// The team that also works the Dental Monitoring inboxes.
export const MONITORING_TEAM: OwnerTeam = 'DM';

const TEAM_HEX: Record<string, string> = {
    Admin: '#4f46e5', // indigo-600
    Clinical: '#0d9488', // teal-600
    DM: '#d97706', // amber-600
    TC: '#ea580c' // orange-600
};

const TEAM_FALLBACK_COLOR = '#9ca3af'; // gray-400

export function teamColor(team: string): string {
    return TEAM_HEX[team] ?? TEAM_FALLBACK_COLOR;
}

// Inflow adds to the queue, throughput drains it — growing = red, shrinking =
// green, so a longer red bar reads as "falling behind" without a legend.
export const FLOW_HEX = {
    inflow: '#e11d48', // rose-600 — cases arriving
    throughput: '#059669' // emerald-600, cases leaving
} as const;

// Buckets are ISO date-only strings; parse as UTC so the label never slips a
// day in negative-offset timezones (same approach as utils/dates.ts).
function parseBucketDate(bucket: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(bucket);
    if (!match) return null;
    const [, year, month, day] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return Number.isNaN(date.getTime()) ? null : date;
}

// Month buckets show the year on January, matching the billing charts' fmtMonth
// so the two dashboards read their x-axes the same way.
function formatBucketLabel(bucket: string, granularity: TeamStatsBucket): string {
    const date = parseBucketDate(bucket);
    if (!date) return bucket;
    if (granularity === 'month') {
        const label = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
        return date.getUTCMonth() === 0
            ? `${label} '${String(date.getUTCFullYear()).slice(2)}`
            : label;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export interface LabeledPoint {
    bucket: string;
    label: string;
}

export interface TrendPoint extends LabeledPoint {
    open: number;
}

export interface FlowPoint {
    bucket: string;
    label: string;
    inflow: number;
    throughput: number;
}

export interface TeamFlow {
    trend: TrendPoint[];
    /** Last trend point minus the first; null when the range has no points. */
    change: number | null;
    series: FlowPoint[];
    inflow: number;
    throughput: number;
}

const STEP_PIE_TOP_COUNT = 5;
export const OTHER_STEPS_KEY = 'other-steps';

export interface StepSlice {
    key: string;
    label: string;
    count: number;
}

export interface TeamWorkload {
    team: OwnerTeam;
    open: number;
    ageBuckets: TeamAgeBucket[];
    /** The team's busiest steps, the rest folded into one Other slice. */
    steps: StepSlice[];
    /** Null when BigQuery is unavailable: the trend and in/out can't be computed. */
    flow: TeamFlow | null;
}

// The backend only sends buckets where a team had transitions, so each team is
// zero-filled to the buckets any team has, giving every card the same x-axis.
function buildFlowSeries(stats: TeamStats, team: OwnerTeam): FlowPoint[] {
    const teamPoints = new Map(
        stats.series.filter((point) => point.team === team).map((point) => [point.bucket, point])
    );
    const buckets = [...new Set(stats.series.map((point) => point.bucket))].sort();
    return buckets.map((bucket) => ({
        bucket,
        label: formatBucketLabel(bucket, stats.bucket),
        inflow: teamPoints.get(bucket)?.inflow ?? 0,
        throughput: teamPoints.get(bucket)?.throughput ?? 0
    }));
}

function buildTeamFlow(stats: TeamStats, team: OwnerTeam): TeamFlow {
    const trend = stats.backlogSeries
        .filter((point) => point.team === team)
        .sort((a, b) => a.bucket.localeCompare(b.bucket))
        .map((point) => ({
            bucket: point.bucket,
            label: formatBucketLabel(point.bucket, stats.bucket),
            open: point.open
        }));
    const series = buildFlowSeries(stats, team);
    return {
        trend,
        change: trend.length > 0 ? trend[trend.length - 1].open - trend[0].open : null,
        series,
        inflow: series.reduce((sum, point) => sum + point.inflow, 0),
        throughput: series.reduce((sum, point) => sum + point.throughput, 0)
    };
}

function buildStepSlices(stats: TeamStats, team: OwnerTeam): StepSlice[] {
    const ranked = stats.stepBacklog
        .filter((row) => row.team === team)
        .map((row) => ({ key: row.statusId, label: row.label, count: row.open }));
    const rest = ranked.slice(STEP_PIE_TOP_COUNT).reduce((sum, slice) => sum + slice.count, 0);
    const top = ranked.slice(0, STEP_PIE_TOP_COUNT);
    return rest > 0 ? [...top, { key: OTHER_STEPS_KEY, label: 'Other', count: rest }] : top;
}

export function buildTeamWorkload(stats: TeamStats, team: OwnerTeam): TeamWorkload {
    return {
        team,
        open: stats.backlog.find((row) => row.team === team)?.open ?? 0,
        ageBuckets: stats.ageBuckets.filter((row) => row.team === team),
        steps: buildStepSlices(stats, team),
        flow: stats.flowAvailable ? buildTeamFlow(stats, team) : null
    };
}

// Bucket order isn't part of the contract, so sort before labelling the x-axis.
export function labelSeries<T extends { bucket: string }>(
    series: readonly T[],
    granularity: TeamStatsBucket
): (T & LabeledPoint)[] {
    return [...series]
        .sort((a, b) => a.bucket.localeCompare(b.bucket))
        .map((point) => ({ ...point, label: formatBucketLabel(point.bucket, granularity) }));
}

const SECONDS_PER_MINUTE = 60;
const NO_MEDIAN = 'n/a';

export function formatCount(value: number): string {
    return value.toLocaleString('en-US');
}

export function formatConversationFlow({
    received,
    treated,
    medianFirstReplyMinutes
}: Pick<ConversationKpis, 'received' | 'treated' | 'medianFirstReplyMinutes'>): string {
    const median =
        medianFirstReplyMinutes === null
            ? null
            : formatDuration(medianFirstReplyMinutes * SECONDS_PER_MINUTE);
    return `${formatCount(received)} in · ${formatCount(treated)} treated · median reply ${median ?? NO_MEDIAN}`;
}

/** Null unless read tracking starts after the first bucket of the date-sorted series. */
export function readTrackingNote(
    openReliableFrom: string | null,
    series: readonly { bucket: string }[]
): string | null {
    if (!openReliableFrom || series.length === 0) return null;
    const reliableFrom = new Date(openReliableFrom);
    if (reliableFrom.getTime() <= Date.parse(series[0].bucket)) return null;
    const date = reliableFrom.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    });
    return `Read tracking starts ${date}; earlier values overstate`;
}
